import { useCallback, useEffect, useState } from "react";
import { History, Paperclip } from "lucide-react";

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Chip } from "@/components/common/Chrome";
import { RecordActions } from "@/components/common/RecordActions";
import { api } from "@/lib/api";
import { dateTime, money, shortDate } from "@/lib/format";

const HIDDEN = new Set(["id", "_id", "tenant_id", "__v"]);
const INTERNAL_KEY = /_id$|_hash$|password/i;
const MONEY_HINTS = /(amount|total|cost|value|budget|spent|committed|balance|gross|limit|price|remaining|variance)/i;
const DATE_HINTS = /(^at$|_at|_on|date)$/i;

const label = (key) => key.replace(/_/g, " ").replace(/\b\w/, (c) => c.toUpperCase());

const scalar = (key, value, currency = "INR") => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return MONEY_HINTS.test(key) ? money(value, currency) : value.toLocaleString();
  if (typeof value === "string" && DATE_HINTS.test(key) && value.length === 10) return shortDate(value);
  if (typeof value === "string" && DATE_HINTS.test(key) && value.length >= 10) return dateTime(value);
  return String(value);
};

const isAttachment = (value) => value && typeof value === "object" && !Array.isArray(value)
  && (value.path || value.filename);

const Field = ({ k, v, currency }) => (
  <div className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5 last:border-0">
    <span className="shrink-0 text-[11px] text-muted-foreground">{label(k)}</span>
    <span className="text-right text-[12px] text-foreground">{scalar(k, v, currency)}</span>
  </div>
);

const NestedTable = ({ k, rows }) => {
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))]
    .filter((c) => !HIDDEN.has(c) && !INTERNAL_KEY.test(c)).slice(0, 6);
  return (
    <div className="space-y-1" data-testid={`detail-list-${k}`}>
      <div className="kicker">{label(k)} · {rows.length}</div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse text-[11px]">
          <thead className="bg-muted/50">
            <tr>{cols.map((c) => (
              <th key={c} className="px-2 py-1.5 text-left font-medium text-muted-foreground">
                {label(c)}
              </th>))}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i}>{cols.map((c) => (
                <td key={c} className="px-2 py-1.5 align-top text-foreground/85">
                  {typeof r[c] === "object" && r[c] !== null
                    ? JSON.stringify(r[c]).slice(0, 60)
                    : scalar(c, r[c])}
                </td>))}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const NAME_KEYS = ["name", "title", "subject_name", "subject", "donor_name", "supplier_name",
                   "person_name", "code", "reference", "item", "email", "description"];
const REF_KEYS = ["reference", "request_ref", "declaration_ref", "po_ref", "job_ref", "booking_ref",
                  "task_ref", "distribution_ref", "exception_id", "receipt_no", "case_number",
                  "staff_ref", "lot_ref", "batch", "code", "budget_key", "id"];

/** The human reference a register row is known by — used as the panel heading and audit key. */
export const refOf = (record) => {
  if (!record || typeof record !== "object") return String(record ?? "");
  for (const key of REF_KEYS) {
    const value = record[key];
    if (typeof value === "string" && value) return value;
  }
  return "";
};

export const DetailSheet = ({ open, onOpenChange, record, reference, title, subtitle,
                              resource, onChanged }) => {
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    if (!open || !reference) { setEvents([]); return; }
    try {
      const res = await api.get("/regulatory/audit", { params: { resource_id: reference, limit: 40 } });
      setEvents(res.data || []);
    } catch { setEvents([]); }
  }, [open, reference]);

  useEffect(() => { load(); }, [load]);

  if (!record) return null;
  const looksInternal = reference && OBJECT_ID.test(reference);
  const shownRef = looksInternal ? "" : reference;
  const heading = title
    || (!looksInternal && reference)
    || NAME_KEYS.map((k) => record[k]).find((v) => typeof v === "string" && v) || "Record";
  const entries = Object.entries(record)
    .filter(([k]) => !HIDDEN.has(k) && !INTERNAL_KEY.test(k));
  const scalars = entries.filter(([, v]) => v === null || typeof v !== "object");
  const currency = typeof record.currency === "string" ? record.currency : "INR";
  const lists = entries.filter(([, v]) => Array.isArray(v) && v.length
    && typeof v[0] === "object");
  const simpleLists = entries.filter(([, v]) => Array.isArray(v) && v.length
    && typeof v[0] !== "object");
  const objects = entries.filter(([, v]) => v && typeof v === "object" && !Array.isArray(v));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right"
                    className="flex w-full max-w-xl flex-col gap-0 overflow-y-auto border-border bg-card p-0"
                    data-testid="detail-sheet">
        <SheetHeader className="sticky top-0 z-10 border-b border-border bg-card/95 px-5 py-4 backdrop-blur">
          <SheetTitle className="flex items-center gap-2 text-base">
            {heading}
          </SheetTitle>
          {(subtitle || shownRef) && (
            <div className="text-[11.5px] text-muted-foreground" data-testid="detail-sheet-subtitle">
              {subtitle}{subtitle && shownRef ? " · " : ""}
              {shownRef && <span className="font-mono">{shownRef}</span>}
            </div>
          )}
          {resource && (
            <div className="pt-1">
              <RecordActions resource={resource} record={record}
                             onDone={async (deleted) => {
                               if (deleted) onOpenChange(false);
                               await onChanged?.();
                             }} />
            </div>
          )}
        </SheetHeader>

        <div className="space-y-5 px-5 py-4">
          <div>
            <div className="kicker mb-1">Details</div>
            {scalars.map(([k, v]) => <Field key={k} k={k} v={v} currency={currency} />)}
          </div>

          {simpleLists.map(([k, v]) => (
            <div key={k} className="space-y-1">
              <div className="kicker">{label(k)}</div>
              <div className="flex flex-wrap gap-1">
                {v.map((item, i) => (
                  <Chip key={i} className="border-border bg-muted text-muted-foreground">
                    {String(item)}
                  </Chip>
                ))}
              </div>
            </div>
          ))}

          {lists.map(([k, v]) => <NestedTable key={k} k={k} rows={v} />)}

          {objects.map(([k, v]) => (
            <div key={k} className="space-y-1">
              <div className="kicker">{label(k)}</div>
              {isAttachment(v) ? (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-2 text-[11.5px]"
                     data-testid={`detail-attachment-${k}`}>
                  <Paperclip className="h-3.5 w-3.5 text-primary" />
                  <span className="text-foreground">{v.filename || v.path}</span>
                  {v.size ? <span className="text-muted-foreground">{Math.round(v.size / 1024)} KB</span> : null}
                </div>
              ) : (
                <div className="rounded-lg border border-border bg-muted/20 p-2">
                  {Object.entries(v).filter(([kk]) => !HIDDEN.has(kk))
                    .map(([kk, vv]) => (
                      <Field key={kk} k={kk}
                             v={typeof vv === "object" && vv !== null ? JSON.stringify(vv) : vv} />
                    ))}
                </div>
              )}
            </div>
          ))}

          <div className="space-y-1">
            <div className="kicker flex items-center gap-1">
              <History className="h-3 w-3" /> Audit history
            </div>
            {events.length === 0 ? (
              <div className="text-[11.5px] text-muted-foreground" data-testid="detail-history-empty">
                No audit events are recorded against this reference.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="detail-history">
                {events.map((e) => (
                  <div key={e.seq} className="rounded-lg border border-border bg-muted/20 p-2 text-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-foreground">{e.action}</span>
                      <span className="text-muted-foreground">{dateTime(e.occurred_at)}</span>
                    </div>
                    <div className="text-muted-foreground">
                      {e.actor_email || "system"}{e.actor_role ? ` (${e.actor_role})` : ""}
                    </div>
                    {e.details && Object.keys(e.details).length > 0 && (
                      <div className="mt-0.5 truncate text-foreground/70">
                        {Object.entries(e.details).slice(0, 4)
                          .map(([k, v]) => `${label(k)}: ${typeof v === "object" ? JSON.stringify(v).slice(0, 40) : v}`)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const useRowDetail = (defaultResource = "", onChanged) => {
  const [state, setState] = useState({ open: false, record: null, reference: "", title: "",
                                       subtitle: "", resource: defaultResource });
  const show = (record, reference, title, subtitle, resource) =>
    setState({ open: true, record, reference: reference || "", title: title || "",
               subtitle: subtitle || "", resource: resource ?? defaultResource });
  const sheet = (
    <DetailSheet open={state.open} record={state.record} reference={state.reference}
                 title={state.title} subtitle={state.subtitle}
                 resource={state.resource} onChanged={onChanged}
                 onOpenChange={(o) => setState((s) => ({ ...s, open: o }))} />
  );
  return { show, sheet };
};

export const stopRow = (e) => e.stopPropagation();

// Row-level clicks must never swallow a click on a control inside the row.
export const rowClick = (fn) => (e) => {
  if (e.target.closest?.('button,a,input,textarea,select,label,[role="combobox"],[role="menuitem"],[role="checkbox"]')) return;
  fn();
};
